/**
 * Taxas de Operadoras e Antecipação — MDR
 * (prototype: erpone_taxas_de_operadoras_e_antecipa_o)
 *
 * Contratos:
 *   GET /finance/mdr-rates?brand&isActive&page&pageSize
 *     → PaginatedResponseDto<MdrRateResponseDto>
 *   POST /finance/mdr-rates            (MdrRateInputDto)
 *   PUT  /finance/mdr-rates/:id        (MdrRateUpdateInputDto)
 *   DELETE /finance/mdr-rates/:id      → { id, deleted }
 *
 * treasury.dto.ts — MdrRateResponseDto:
 *   id, brand, debitRate, creditCashRate, installments2To6Rate,
 *   installments7To12Rate, anticipationRate (opcional/null), isActive, ...
 *
 * O protótipo edita taxas "inline" por linha e salva em lote; o backend só
 * expõe PUT por taxa, então a edição é por linha via modal (PUT /:id) — a
 * mesma informação, sem inventar um endpoint de lote.
 */
import { useState } from 'react';
import {
  Card,
  CardHeader,
  Badge,
  Button,
  LoadingSkeleton,
  Modal,
  Input,
} from '@nexora/design-system';
import {
  useMdrRates,
  useCreateMdrRate,
  useUpdateMdrRate,
  useDeleteMdrRate,
} from './treasuryHooks';
import type { MdrRate } from './treasuryTypes';
import { formatNumber } from '../../../shared/utils/format';

interface MdrForm {
  brand: string;
  debitRate: string;
  creditCashRate: string;
  installments2To6Rate: string;
  installments7To12Rate: string;
  anticipationRate: string;
  isActive: boolean;
}

const emptyForm: MdrForm = {
  brand: '',
  debitRate: '',
  creditCashRate: '',
  installments2To6Rate: '',
  installments7To12Rate: '',
  anticipationRate: '',
  isActive: true,
};

function toForm(r: MdrRate): MdrForm {
  return {
    brand: r.brand,
    debitRate: String(r.debitRate),
    creditCashRate: String(r.creditCashRate),
    installments2To6Rate: String(r.installments2To6Rate),
    installments7To12Rate: String(r.installments7To12Rate),
    anticipationRate: r.anticipationRate != null ? String(r.anticipationRate) : '',
    isActive: r.isActive,
  };
}

function pct(v: number | null | undefined): string {
  return formatNumber(v ?? 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
}

const RATE_COLUMNS: { key: RateField; label: string }[] = [
  { key: 'debitRate', label: 'Débito' },
  { key: 'creditCashRate', label: 'Crédito à Vista' },
  { key: 'installments2To6Rate', label: 'Parcelado (2 a 6x)' },
  { key: 'installments7To12Rate', label: 'Parcelado (7 a 12x)' },
];

/** Campos numéricos de taxa do formulário (todos string no form). */
type RateField = 'debitRate' | 'creditCashRate' | 'installments2To6Rate' | 'installments7To12Rate' | 'anticipationRate';

export function MdrRatesPage(): JSX.Element {
  const { data, isLoading, error, refetch } = useMdrRates();
  const createMutation = useCreateMdrRate();
  const updateMutation = useUpdateMdrRate();
  const deleteMutation = useDeleteMdrRate();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<MdrRate | null>(null);
  const [form, setForm] = useState<MdrForm>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<MdrRate | null>(null);
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);

  const rates = data?.data ?? [];
  const total = data?.total ?? 0;

  const showToast = (message: string, variant: 'success' | 'error'): void => {
    setToast({ message, variant });
    setTimeout(() => setToast(null), 4000);
  };

  const openCreate = (): void => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (r: MdrRate): void => {
    setEditing(r);
    setForm(toForm(r));
    setModalOpen(true);
  };

  const handleSave = async (): Promise<void> => {
    if (!form.brand) {
      showToast('Informe a bandeira/operadora.', 'error');
      return;
    }
    const num = (v: string): number | undefined => (v === '' ? undefined : Number(v));
    const payload = {
      brand: form.brand,
      debitRate: num(form.debitRate),
      creditCashRate: num(form.creditCashRate),
      installments2To6Rate: num(form.installments2To6Rate),
      installments7To12Rate: num(form.installments7To12Rate),
      ...(form.anticipationRate !== '' ? { anticipationRate: num(form.anticipationRate) } : {}),
    };
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, ...payload, isActive: form.isActive });
        showToast('Taxas MDR atualizadas.', 'success');
      } else {
        await createMutation.mutateAsync(payload);
        showToast('Taxa MDR criada.', 'success');
      }
      setModalOpen(false);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Erro ao salvar taxa MDR.', 'error');
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      showToast('Taxa MDR excluída.', 'success');
      setDeleteTarget(null);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Erro ao excluir taxa MDR.', 'error');
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-surface-bright p-md md:p-lg">
      {/* ── Page header ── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Taxas de Operadoras e Antecipação</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Configuração de MDR (Merchant Discount Rate) por bandeira e faixa de parcelamento.
          </p>
        </div>
        <Button onClick={openCreate}>+ Nova Taxa MDR</Button>
      </div>

      {/* ── Loading ── */}
      {isLoading && (
        <Card padding="2rem">
          <LoadingSkeleton count={4} height="48px" />
          <p style={{ color: '#43474e', fontSize: '0.875rem' }}>Carregando taxas MDR...</p>
        </Card>
      )}

      {/* ── Error ── */}
      {error && !isLoading && (
        <Card padding="2rem" className="text-centered">
          <p style={{ color: '#ba1a1a', fontWeight: 600, margin: 0 }}>Erro ao carregar taxas MDR</p>
          <p style={{ fontSize: '0.875rem', color: '#74777f' }}>{(error as Error).message}</p>
          <Button size="sm" variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
        </Card>
      )}

      {/* ── Empty honesto ── */}
      {!isLoading && !error && rates.length === 0 && (
        <Card padding="2rem" className="text-centered muted">
          <p style={{ margin: 0, fontSize: '0.875rem' }}>Nenhuma taxa MDR cadastrada.</p>
          <Button size="sm" variant="outline" onClick={openCreate} style={{ marginTop: '1rem' }}>
            Criar primeira taxa
          </Button>
        </Card>
      )}

      {/* ── Tabela ── */}
      {!isLoading && !error && rates.length > 0 && (
        <Card variant="outlined" padding="0" className="overflow-hidden">
          <CardHeader className="px-md pt-md" action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {total} {total === 1 ? 'bandeira' : 'bandeiras'}
            </span>
          }>
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Configuração de MDR</h3>
          </CardHeader>
          <div className="tr-table-wrap">
            <table className="tr-table">
              <thead>
                <tr>
                  <th>Bandeira</th>
                  {RATE_COLUMNS.map((c) => (
                    <th key={c.key} className="text-right">{c.label}</th>
                  ))}
                  <th className="text-right">Antecipação</th>
                  <th>Ativa</th>
                  <th className="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {rates.map((r) => (
                  <tr key={r.id}>
                    <td className="tr-cell-strong">{r.brand}</td>
                    <td className="tr-cell-mono text-right tabular-nums">{pct(r.debitRate)}</td>
                    <td className="tr-cell-mono text-right tabular-nums">{pct(r.creditCashRate)}</td>
                    <td className="tr-cell-mono text-right tabular-nums">{pct(r.installments2To6Rate)}</td>
                    <td className="tr-cell-mono text-right tabular-nums">{pct(r.installments7To12Rate)}</td>
                    <td className="tr-cell-mono text-right tabular-nums">
                      {r.anticipationRate != null ? pct(r.anticipationRate) : '—'}
                    </td>
                    <td>
                      {r.isActive ? (
                        <Badge variant="success" size="sm">Ativa</Badge>
                      ) : (
                        <Badge variant="neutral" size="sm">Inativa</Badge>
                      )}
                    </td>
                    <td className="text-center">
                      <div className="tr-actions">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(r)}>Editar</Button>
                        <Button size="sm" variant="ghost" style={{ color: '#ba1a1a' }} onClick={() => setDeleteTarget(r)}>
                          Excluir
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── Modal: Criar/editar ── */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Editar — ${editing.brand}` : 'Nova taxa MDR'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={() => void handleSave()} loading={createMutation.isPending || updateMutation.isPending}>
              {editing ? 'Atualizar' : 'Criar'}
            </Button>
          </>
        }
      >
        <div className="tr-form">
          <Input
            label="Bandeira / Operadora *"
            value={form.brand}
            onChange={(e) => setForm({ ...form, brand: e.target.value })}
            disabled={!!editing}
            placeholder="VISA, MASTERCARD, ELO..."
          />
          <div className="tr-form__grid">
            {RATE_COLUMNS.map((c) => (
              <Input
                key={c.key}
                label={`${c.label} (%)`}
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={form[c.key]}
                onChange={(e) => setForm({ ...form, [c.key]: e.target.value })}
              />
            ))}
            <Input
              label="Antecipação (%)"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.anticipationRate}
              onChange={(e) => setForm({ ...form, anticipationRate: e.target.value })}
              placeholder="Opcional"
            />
          </div>
          <label className="tr-form__toggle">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            Taxa ativa
          </label>
        </div>
      </Modal>

      {/* ── Modal: Excluir ── */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Excluir taxa MDR"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button variant="danger" onClick={() => void handleDelete()} loading={deleteMutation.isPending}>
              Excluir
            </Button>
          </>
        }
      >
        <p style={{ fontSize: '0.875rem', color: '#43474e', margin: 0 }}>
          Excluir a configuração MDR da bandeira <strong>{deleteTarget?.brand}</strong>? Esta ação não pode ser desfeita.
        </p>
      </Modal>

      {/* ── Toast ── */}
      {toast && (
        <div role="alert" className="tr-toast" style={{
          background: toast.variant === 'success' ? '#d3f5d6' : '#ffdad6',
          border: `1px solid ${toast.variant === 'success' ? '#b6ebbb' : '#ffb4ab'}`,
          color: toast.variant === 'success' ? '#2e7d32' : '#ba1a1a',
        }}>
          {toast.message}
          <button onClick={() => setToast(null)} aria-label="Fechar" className="tr-toast__close">&times;</button>
        </div>
      )}

      <style>{`
        .text-centered { text-align: center; }
        .muted { color: #74777f; }
        .tr-table-wrap { overflow-x: auto; }
        .tr-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .tr-table th {
          padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e;
          font-weight: 600; font-size: 0.75rem; text-transform: uppercase;
          letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5;
          text-align: left; white-space: nowrap; position: sticky; top: 0; z-index: 1;
        }
        .tr-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .tr-table tbody tr:hover td { background: #eceef0; }
        .tr-cell-strong { font-weight: 600; }
        .tr-cell-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 0.8125rem; }
        .tr-actions { display: flex; gap: 0.25rem; justify-content: center; flex-wrap: wrap; }
        .tr-form { display: flex; flex-direction: column; gap: 1rem; min-width: 460px; }
        .tr-form__grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.75rem; }
        .tr-form__toggle { display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; color: #191c1e; }
        .tr-toast {
          position: fixed; bottom: 1.5rem; right: 1.5rem; padding: 0.75rem 1rem;
          border-radius: 8px; font-size: 0.875rem; font-weight: 500;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 10000; max-width: 400px;
        }
        .tr-toast__close { margin-left: 0.75rem; background: none; border: none; cursor: pointer; color: inherit; font-size: 1rem; padding: 0; line-height: 1; }
      `}</style>
    </div>
  );
}